import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// P9.6 — Fail-loud guard: a missing REACT_APP_BACKEND_URL silently breaks every API call
// (axios baseURL becomes "undefined/api"). Log + render a sticky banner so it's caught on first paint.
if (!BACKEND_URL || BACKEND_URL === 'undefined') {
  // eslint-disable-next-line no-console
  console.error(
    '[E1-ERROR] REACT_APP_BACKEND_URL is not set. The frontend cannot reach the API.\n' +
    'Set it in /app/frontend/.env and restart the frontend supervisor process.'
  );
  if (typeof document !== 'undefined') {
    const showBanner = () => {
      if (document.getElementById('backend-url-missing-banner')) return;
      const el = document.createElement('div');
      el.id = 'backend-url-missing-banner';
      el.setAttribute('data-testid', 'backend-url-missing-banner');
      el.style.cssText =
        'position:fixed;top:0;left:0;right:0;z-index:99999;' +
        'padding:10px 16px;background:#b91c1c;color:#fff;' +
        'font-family:system-ui,sans-serif;font-size:13px;font-weight:600;' +
        'text-align:center;box-shadow:0 2px 8px rgba(0,0,0,.35);';
      el.textContent =
        'BACKEND URL NOT CONFIGURED — frontend cannot reach API. Restart the frontend after setting REACT_APP_BACKEND_URL.';
      document.body.appendChild(el);
    };
    if (document.body) showBanner();
    else document.addEventListener('DOMContentLoaded', showBanner);
  }
}

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// A read that fails on the server or the network used to leave the screen
// drawn as "no records" / 0 / "none allotted", which says something false
// about the data.  Every such failure now shows one load-error toast (one per
// address every few seconds); 4xx answers are left to the screen.
const _recentLoadErrors = new Map();
function reportLoadFailure(error) {
  const cfg = error?.config || {};
  if ((cfg.method || 'get').toLowerCase() !== 'get' || cfg.silent) return;
  const status = error?.response?.status;
  if (status && status < 500) return;
  const key = String(cfg.url || '').split('?')[0];
  const now = Date.now();
  if (now - (_recentLoadErrors.get(key) || 0) < 5000) return;
  _recentLoadErrors.set(key, now);
  const detail = error?.response?.data?.detail;
  const msg = (detail && (detail.message || (typeof detail === 'string' ? detail : null))) || error?.message || 'Request failed';
  // Lazy import keeps this module free of UI dependencies at load time.
  import('sonner').then(({ toast }) => toast.error(`Could not load data (${key}): ${msg}. The list may be incomplete — retry.`))
    .catch(() => {});
}

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    reportLoadFailure(error);
    if (error.response?.status === 401) {
      // Clear local storage and redirect to login
      // Phase 2 — never redirect away from /print/... (headless PDF route uses
      // a short-lived print token via /api/auth/print-token/verify, not the
      // standard session cookie).
      const onPrintRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/print/');
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      if (!onPrintRoute && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export function formatApiError(error) {
  const detail = error.response?.data?.detail || error.response?.data?.message;
  if (detail == null) return error.message || 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  // Feb 2026 Issue #7 — Backend often returns detail as {code, message, ...}
  // (e.g. rm_issue.coil_over_issue, inspection guards). Previously we fell
  // through to `String(detail)` which rendered "[object Object]" in toasts.
  if (detail && typeof detail === 'object') {
    if (typeof detail.message === 'string') return detail.message;
    if (typeof detail.error === 'string') return detail.error;
    try { return JSON.stringify(detail); } catch { /* fallthrough */ }
  }
  return String(detail);
}

export default api;
